import { Box, Flex, Stack, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  BidStatus,
  SourcingEventStatus,
  useBeginEvaluationMutation,
  useGetSourcingEventQuery,
  type BuyerBidDetailDto,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { hasEarlyBidAccess, isMinimumRequirementReviewOpen } from "../bidAccess";
import { NotReadyCard } from "../NotReadyCard";
import { isBidDeadlinePassed } from "../supplierRows";
import { getBidMinimumRequirementProgress, getBidMinimumRequirementRows } from "./bidMinimumRequirements";
import { HiddenEntryDocumentationNotice } from "./HiddenEntryDocumentationNotice";
import { MinimumRequirementReviewCard } from "./MinimumRequirementReviewCard";
import { useBuyerMinimumRequirementReviewEditor } from "./useBuyerMinimumRequirementReviewEditor";

interface Props {
  eventId: string;
  bidId: string;
  detail: BuyerBidDetailDto;
}

export const MinimumRequirementsTab = ({ eventId, bidId, detail }: Props) => {
  const { t } = useTranslation();
  const editor = useBuyerMinimumRequirementReviewEditor(eventId, bidId, detail);
  const { hasRequirements, fulfilled, notFulfilled, pending, total } = useMemo(
    () => getBidMinimumRequirementProgress(detail),
    [detail],
  );
  const rows = useMemo(() => getBidMinimumRequirementRows(detail), [detail]);
  const {
    data: event,
    isError: eventFailedToLoad,
    isFetching: isReloadingEvent,
    refetch: reloadEvent,
  } = useGetSourcingEventQuery({ eventId });
  const isRejected = detail.bid.status === BidStatus.Rejected;
  const reviewClosed = event != null && !isMinimumRequirementReviewOpen(event);
  const isReadOnly = isRejected || event == null || reviewClosed;

  if (!hasRequirements) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="2"
        py="16"
        rounded="xl"
        bg="smBackground"
        border="1px dashed"
        borderColor="smBorder"
        color="smMuted"
      >
        <Text fontWeight="medium">{t("No minimum requirements")}</Text>
      </Flex>
    );
  }

  return (
    <Stack spacing="6">
      {!isRejected && eventFailedToLoad && event == null && (
        <NotReadyCard
          title={String(t("The assessments are locked"))}
          description={String(t("The procurement could not be loaded. Try again to record the assessments."))}
          action={{ label: String(t("Try again")), onClick: reloadEvent, isLoading: isReloadingEvent }}
        />
      )}
      {!isRejected && reviewClosed && <ReviewClosedNotice event={event} />}
      {event?.entriesAnonymous && <HiddenEntryDocumentationNotice />}
      <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" px="5" py="4">
        <Text color="smMuted" fontSize="sm">
          {pending === 0
            ? fulfilled === total
              ? t("All {{total}} requirements are fulfilled", { total })
              : t("{{count}} of {{total}} fulfilled", { count: fulfilled, total })
            : t("{{count}} of {{total}} reviewed", { count: fulfilled + notFulfilled, total })}
        </Text>
      </Box>

      {rows.map(({ requirement, response }) => {
        const id = requirement.id ?? "";
        return (
          <MinimumRequirementReviewCard
            key={id}
            id={id}
            title={requirement.title}
            description={requirement.description}
            responseType={requirement.responseType}
            supplierAnswer={response?.answer}
            supplierComment={response?.comment ?? null}
            supplierAttachments={response?.attachments ?? null}
            attachmentsHidden={event?.entriesAnonymous ?? false}
            verdict={editor.state.verdicts[id] ?? null}
            comment={editor.state.comments[id] ?? ""}
            onVerdictChange={editor.setVerdict}
            onCommentChange={editor.setComment}
            isReadOnly={isReadOnly}
          />
        );
      })}
    </Stack>
  );
};

const ReviewClosedNotice = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const [beginEvaluation, { isLoading }] = useBeginEvaluationMutation();

  if (event.status !== SourcingEventStatus.Published) {
    return (
      <NotReadyCard
        title={String(t("The assessments are locked"))}
        description={String(t("Minimum requirements can only be assessed while the evaluation is open."))}
      />
    );
  }

  const deadlinePassed = isBidDeadlinePassed(event);
  const canBegin = deadlinePassed || hasEarlyBidAccess(event, selectedOrg);
  const begin = async () => {
    try {
      await beginEvaluation({ eventId: event.id }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <NotReadyCard
      title={String(t("The evaluation has not started"))}
      description={String(
        !canBegin
          ? t("You can begin evaluation once the submission deadline has passed.")
          : deadlinePassed
            ? t("Start the evaluation to record whether each requirement is met.")
            : t(
                "Start the evaluation to record whether each requirement is met - suppliers can still deliver until the deadline.",
              ),
      )}
      action={canBegin ? { label: String(t("Begin evaluation")), onClick: begin, isLoading } : undefined}
    />
  );
};
